/* Coral Slub homepage navigation. Standalone: no filtering or cart API engine. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('coral-home-mobile-nav')) return;

  class CoralHomeMobileNav extends HTMLElement {
    connectedCallback() {
      if (this._ready || this.dataset.pageType !== 'index') return;
      this._ready = true;
      this.dialog = this.querySelector('[data-csh-dialog]');
      this.primaryBar = this.querySelector(':scope > .cs-home__bar');
      if (!this.dialog || !this.primaryBar) return;
      this._media = window.matchMedia('(max-width: 991px)');
      this._onClick = this.handleClick.bind(this);
      this._onKeydown = this.handleKeydown.bind(this);
      this._onMedia = this.refreshViewport.bind(this);
      this._onCancel = (event) => { event.preventDefault(); this.closePanel(); };
      this._onClose = () => { if (!this.dialog.open) this.finishClose(false); };
      this._onPageHide = () => this.closePanel(false);
      this._onPageShow = () => { this.bindThemeObservers(); this.refreshViewport(); };
      this._onSectionLoad = () => this.bindThemeObservers();
      // Capture: close our modal before handing control to existing account handlers.
      this.addEventListener('click', this._onClick, true);
      this.addEventListener('keydown', this._onKeydown);
      this.dialog.addEventListener('cancel', this._onCancel);
      this.dialog.addEventListener('close', this._onClose);
      this._media.addEventListener('change', this._onMedia);
      window.addEventListener('pagehide', this._onPageHide);
      window.addEventListener('pageshow', this._onPageShow);
      document.addEventListener('shopify:section:load', this._onSectionLoad);
      this.bindThemeObservers();
      this.refreshViewport();
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this.closePanel(false);
      this._themeObserver?.disconnect();
      this._countObserver?.disconnect();
      cancelAnimationFrame(this._themeFrame);
      this.removeEventListener('click', this._onClick, true);
      this.removeEventListener('keydown', this._onKeydown);
      this.dialog?.removeEventListener('cancel', this._onCancel);
      this.dialog?.removeEventListener('close', this._onClose);
      this._media?.removeEventListener('change', this._onMedia);
      window.removeEventListener('pagehide', this._onPageHide);
      window.removeEventListener('pageshow', this._onPageShow);
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      document.body.classList.remove('cs-home-nav-active');
      document.body.style.removeProperty('--csh-page-bar-height');
      this._ready = false;
    }

    refreshViewport() {
      if (!this._media.matches) this.closePanel(false);
      document.body.classList.toggle('cs-home-nav-active', this._media.matches);
      if (this._media.matches) {
        const height = getComputedStyle(this).getPropertyValue('--csh-bar-height').trim() || '88px';
        document.body.style.setProperty('--csh-page-bar-height', height);
      } else {
        document.body.style.removeProperty('--csh-page-bar-height');
      }
      this.checkThemeOverlay();
    }

    handleClick(event) {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target.closest('a,button') : null;
      if (!target || !this.contains(target)) return;
      if (target.hasAttribute('data-csh-close')) {
        event.preventDefault(); this.closePanel(); return;
      }
      if (target.hasAttribute('data-csh-menu-toggle')) {
        const submenu = document.getElementById(target.getAttribute('aria-controls'));
        if (submenu && this.contains(submenu)) {
          event.preventDefault();
          const expanded = target.getAttribute('aria-expanded') !== 'true';
          target.setAttribute('aria-expanded', String(expanded));
          submenu.hidden = !expanded;
        }
        return;
      }
      if (target.hasAttribute('data-csh-tab')) {
        event.preventDefault(); this.activateTab(target); return;
      }
      if (target.hasAttribute('data-csh-open')) {
        const name = target.dataset.cshOpen;
        if (!this._media.matches || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        if (this.dialog.open && this._screen === name) this.closePanel();
        else this.openPanel(name, target);
        return;
      }
      if (target.hasAttribute('data-csh-home') && this.dialog.open) {
        event.preventDefault(); this.closePanel(); return;
      }
      if (target.hasAttribute('data-csh-cart')) {
        this.closePanel(false);
          if (this.openExistingCart()) {
    event.preventDefault();
  }

        // The sticky Cart uses its real, locale-aware href by default.
        // Retain the legacy drawer option only when direct navigation is disabled.
        if (this.dataset.cartPageOnly === 'false' && this.openExistingCart()) event.preventDefault();
        return;
      }
      if (target.hasAttribute('data-csh-account')) {
        this.closePanel(false);
        const handler = typeof window.kpHandleLogin === 'function' ? window.kpHandleLogin
          : typeof window.handleKpAndShopifyLogin === 'function' ? window.handleKpAndShopifyLogin : null;
        if (handler) {
          event.preventDefault();
          // Avoid executing the existing direct account listener a second time.
          event.stopPropagation();
          try {
            const result = handler(target.getAttribute('href'));
            if (result && typeof result.catch === 'function') result.catch(() => window.location.assign(target.href));
          } catch (_) { window.location.assign(target.href); }
        }
        // Without Kwikpass, keep the original account link and its native listeners.
        return;
      }
      if (target.tagName === 'A' && this.dialog.open) this.closePanel(false);
    }

    openPanel(name, trigger) {
      const screen = this.querySelector(`[data-csh-screen="${name}"]`);
      if (!screen || !this._media.matches) return;
      if (!this.dialog.open) {
        if (this.hasThemeOverlay()) return;
        this._returnTo = this.primaryBar.querySelector(`[data-csh-open="${name}"]`) || trigger;
      }
      this._screen = name;
      this.querySelectorAll('[data-csh-screen]').forEach((node) => { node.hidden = node !== screen; });
      this.dialog.setAttribute('aria-label', screen.querySelector('[data-csh-heading]')?.textContent.trim() || name);
      if (!this.dialog.open) {
        try { this.dialog.showModal(); } catch (_) { return; }
      }
      document.documentElement.classList.add('cs-home-menu-open');
      this.querySelectorAll('[data-csh-open]').forEach((node) => {
        node.setAttribute('aria-expanded', String(node.dataset.cshOpen === name));
      });
      screen.querySelector('[data-csh-heading]')?.focus({ preventScroll: true });
    }

    closePanel(restoreFocus = true) {
      if (!this.dialog?.open) return;
      this.dialog.close();
      this.finishClose(restoreFocus);
    }

    finishClose(restoreFocus) {
      document.documentElement.classList.remove('cs-home-menu-open');
      this.querySelectorAll('[data-csh-open]').forEach((node) => node.setAttribute('aria-expanded', 'false'));
      this._screen = null;
      if (restoreFocus && this._media.matches && this._returnTo?.isConnected) this._returnTo.focus({ preventScroll: true });
    }

    activateTab(tab) {
      this.querySelectorAll('[data-csh-tab]').forEach((node) => {
        const selected = node === tab;
        node.setAttribute('aria-selected', String(selected));
        node.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(node.getAttribute('aria-controls'));
        if (panel && this.contains(panel)) panel.hidden = !selected;
      });
      this.querySelector('.cs-home__category-content')?.scrollTo({ top: 0 });
    }

    handleKeydown(event) {
      if (event.key === 'Tab' && this.dialog.open) {
        const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
          .filter((node) => node.tabIndex >= 0 && node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (first && event.shiftKey && (active === first || !focusable.includes(active))) {
          event.preventDefault(); last.focus();
        } else if (first && !event.shiftKey && active === last) {
          event.preventDefault(); first.focus();
        }
        return;
      }
      const tab = event.target.closest?.('[data-csh-tab]');
      if (!tab) return;
      const tabs = [...this.querySelectorAll('[data-csh-tab]')];
      let index = tabs.indexOf(tab);
      if (['ArrowDown', 'ArrowRight'].includes(event.key)) index = (index + 1) % tabs.length;
      else if (['ArrowUp', 'ArrowLeft'].includes(event.key)) index = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else return;
      event.preventDefault();
      this.activateTab(tabs[index]);
      tabs[index].focus();
    }

    openExistingCart() {
      if (this.dataset.useCartDrawer !== 'true' || !window.theme?.miniCart) return false;
      const containers = [...document.querySelectorAll('.site-header .js-mini-cart[data-cartmini="true"]')];
      const cart = containers.find((node) => node.getClientRects().length > 0);
      const trigger = cart?.querySelector('.js-toggle-cart');
      if (!trigger) return false;
      // Existing theme.miniCart delegates this click. Do not build or fetch a new cart.
      if (!cart.classList.contains('active')) trigger.click();
      const opened = cart.classList.contains('active');
      this.checkThemeOverlay();
      return opened;
    }

    bindThemeObservers() {
      this._themeObserver?.disconnect();
      this._countObserver?.disconnect();
      this._themeObserver = new MutationObserver(() => {
        cancelAnimationFrame(this._themeFrame);
        this._themeFrame = requestAnimationFrame(() => this.checkThemeOverlay());
      });
      [document.documentElement, document.body, ...document.querySelectorAll('.site-header .js-mini-cart, #NavDrawer, .modal, dialog')]
        .filter((node) => !this.contains(node))
        .forEach((node) => this._themeObserver.observe(node, { attributes: true, attributeFilter: ['class', 'open', 'style'] }));
      this._countSource = document.querySelector('.site-header .js-toggle-cart .js-cart-count');
      if (this._countSource) {
        const sync = () => {
          const match = this._countSource.textContent.match(/\d+/);
          if (!match) return;
          const count = Number(match[0]);
          if (!Number.isSafeInteger(count) || count < 0) return;
          this.querySelectorAll('[data-csh-cart-count]').forEach((badge) => {
            badge.textContent = String(count);
            badge = count === 0;
          });
        };
        this._countObserver = new MutationObserver(sync);
        this._countObserver.observe(this._countSource, { childList: true, characterData: true, subtree: true });
        sync();
      }
      this.checkThemeOverlay();
    }

    hasThemeOverlay() {
      if (document.documentElement.classList.contains('js-drawer-open') || document.body.classList.contains('modal-open')) return true;
      return [...document.querySelectorAll('.site-header .js-mini-cart.active, .modal.show, .modal.in, dialog[open]')]
        .some((node) => !this.contains(node) && node.getClientRects().length > 0);
    }

    checkThemeOverlay() {
      const active = this.hasThemeOverlay();
      this.toggleAttribute('data-external-overlay', active);
      if (active && this.dialog.open) this.closePanel(false);
    }
  }
  customElements.define('coral-home-mobile-nav', CoralHomeMobileNav);
})();
